import { SidebarChat, type UseSidebarChatReturn, useSidebarChat } from '@platform/chat';
import { upsertDocuments } from '@platform/documents';
import { EditableSummary } from '@platform/editor';
import { FEATURE_FLAGS, useFeatureFlag } from '@platform/feature-flags';
import { copyMarkdownToClipboard } from '@platform/markdown';
import {
  clearActiveRun,
  selectIsHydratingSummaryReplay,
  selectIsSummaryStreaming,
  selectSummaryActiveRun,
  selectSummarySelection,
  setEditing,
  setSelected,
} from '@platform/summary-ui';
import { type EditableSummaryEntity } from '@platform/timeline';
import { type FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import type { SummaryPageContext } from '@shared/types/pageContext';
import { Alert, Button, Icon, IconButton, Paper, Snackbar, Tooltip, Typography } from '@shared/ui';
import React from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useBreadcrumbOverrides } from '../../layout/navigation/BreadcrumbOverridesContext';
import { isFullAccessUser } from '../../platform/auth/userRoles';
import { analytics } from '../../services/analytics';
import { useGetTeamQuery } from '../../store/api/apiSlice';
import { useLazyBatchDocumentsQuery } from '../../store/api/rpcSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  useGetArtifactQuery,
  useLazyGetArtifactQuery,
  useListArtifactVersionsQuery,
  useShareArtifactMutation,
  useUpdateArtifactMutation,
} from '../coach-artifacts/api/artifactApi';
import {
  type FeedbackType,
  useCreateArtifactFeedbackMutation,
  useGetLatestArtifactFeedbackQuery,
} from '../coach-artifacts/api/artifactFeedbackApi';
import ArtifactPageWithChatLayout from '../coach-artifacts/components/ArtifactPageWithChatLayout';
import { ArtifactVersionDropdown } from '../coach-artifacts/components/ArtifactVersionDropdown';
import { FeedbackModal } from '../coach-artifacts/components/FeedbackModal';
import { ShareButton } from '../coach-artifacts/components/ShareButton';
import { ShareConfirmationDialog } from '../coach-artifacts/components/ShareConfirmationDialog';
import { getArtifactTypeLabel } from '../coach-artifacts/utils/artifactLabels';
import { resolveFeedbackTarget } from '../coach-artifacts/utils/feedbackTarget';
import {
  getProfileForSummaryDocumentCount,
  isProfileShareable,
  PROFILE_SLUGS,
  profileSlugToArtifactType,
} from '../coach-artifacts/utils/profileSlugs';
import { formatShareSessionDateFromTranscriptSessions } from '../coach-artifacts/utils/shareSessionDate';
import { getMemberDisplayName } from '../members/types';
import { toCurrentArtifactContext } from './currentArtifactContext';
import { buildSummaryBreadcrumbOverrides } from './summaryBreadcrumbOverrides';
import { isAwaitingPersistedVersion, shouldShowStreamingSummary } from './summaryDisplay';
import { parseTranscriptMetadataParams, resolveSubjectNameForBreadcrumb } from './summaryMetadata';

type SummaryBreadcrumbOverridesProps = {
  memberLabel: string | null;
  memberId: string | null;
  summaryLabel: string | null;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;
const ARTIFACT_ORIGIN_MANUAL_DRIVE = 'manual_drive';

function parseDocumentIds(searchParams: URLSearchParams): string[] {
  const docsParam = searchParams.get('docs');
  return docsParam ? docsParam.split(',').filter(Boolean) : [];
}

function isDocsOnlySummaryRequest(searchParams: URLSearchParams): boolean {
  return (
    parseDocumentIds(searchParams).length > 0 &&
    parseTranscriptMetadataParams(searchParams) === null
  );
}

function isMemberReflectionProfile(profile: string | null): boolean {
  const normalized = profile?.trim() ?? '';
  return (
    normalized === PROFILE_SLUGS.MEMBER_REFLECTION ||
    normalized === PROFILE_SLUGS.CHEM_CALL_REFLECTION_MEMBER ||
    normalized === PROFILE_SLUGS.MEMBER_REFLECTION_ARC
  );
}

function legacyMemberReflectionRedirectTarget(searchParams: URLSearchParams): string {
  const subjectId = searchParams.get('subject_id')?.trim();
  if (subjectId) {
    return `/members/${encodeURIComponent(subjectId)}`;
  }

  return '/members';
}

function parseCanonicalConversationID(value: string | null): string | null {
  const normalized = value?.trim() ?? '';
  if (normalized === '' || !UUID_PATTERN.test(normalized)) {
    return null;
  }
  return normalized;
}

function parseArtifactID(value: string | null): string | null {
  const normalized = value?.trim() ?? '';
  return normalized === '' ? null : normalized;
}

function parsePositiveRevision(value: string | null): number | null {
  const normalized = value?.trim() ?? '';
  if (normalized === '' || !POSITIVE_INTEGER_PATTERN.test(normalized)) {
    return null;
  }
  return Number(normalized);
}

const SummaryBreadcrumbOverrides = ({
  memberLabel,
  memberId,
  summaryLabel,
}: SummaryBreadcrumbOverridesProps): React.ReactElement | null => {
  const { setBreadcrumbOverrides } = useBreadcrumbOverrides();

  React.useEffect(() => {
    const next = buildSummaryBreadcrumbOverrides({
      memberId,
      memberLabel,
      summaryLabel,
    });
    setBreadcrumbOverrides(next);
  }, [memberId, memberLabel, setBreadcrumbOverrides, summaryLabel]);

  return null;
};

export default function SummaryPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const currentUser = useAppSelector((s) => s.auth.user);
  const isManualDriveGenerationEnabled = useFeatureFlag(
    FEATURE_FLAGS.MANUAL_DRIVE_ARTIFACT_GENERATION
  );

  if (isMemberReflectionProfile(searchParams.get('profile')) && isFullAccessUser(currentUser)) {
    return <Navigate replace to={legacyMemberReflectionRedirectTarget(searchParams)} />;
  }

  if (isDocsOnlySummaryRequest(searchParams) && !isManualDriveGenerationEnabled) {
    return <Navigate replace to="/no-access" />;
  }

  return <SummaryPageContent />;
}

function SummaryPageContent(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const personId = useAppSelector((s) => s.auth.user?.id) || '';
  const profileParam = searchParams.get('profile');
  const initialResumeStateRef = React.useRef<{
    conversationId: string | null;
    artifactId: string | null;
    revision: number | null;
  } | null>(null);
  if (initialResumeStateRef.current === null) {
    initialResumeStateRef.current = {
      conversationId: parseCanonicalConversationID(searchParams.get('conversation_id')),
      artifactId: parseArtifactID(searchParams.get('artifact_id')),
      revision: parsePositiveRevision(searchParams.get('revision')),
    };
  }
  const resumableConversationID = initialResumeStateRef.current.conversationId;
  const restoredArtifactID = initialResumeStateRef.current.artifactId;
  const restoredRevision = initialResumeStateRef.current.revision;
  const hasResumableConversation = resumableConversationID !== null;
  const hasCompleteArtifactRestoreState =
    hasResumableConversation && restoredArtifactID !== null && restoredRevision !== null;
  const { data: teamData } = useGetTeamQuery();

  // Parse document IDs from query params
  const documentIds = React.useMemo(() => parseDocumentIds(searchParams), [searchParams]);

  // Parse transcript metadata from query params (for artifact title generation)
  const transcriptMetadata = React.useMemo(
    () => parseTranscriptMetadataParams(searchParams),
    [searchParams]
  );
  const summaryMemberLabel = React.useMemo(
    () =>
      resolveSubjectNameForBreadcrumb({
        subjectId: transcriptMetadata?.subjectId ?? null,
        subjectName: transcriptMetadata?.subjectName ?? null,
        teamMembers: teamData?.members ?? null,
      }),
    [teamData?.members, transcriptMetadata?.subjectId, transcriptMetadata?.subjectName]
  );

  // Memoized selector for relevant documents - avoids creating new arrays on each render
  const relevantDocsSelector = React.useMemo(
    () => (s: { documents: { byId: Record<string, any> } }) => {
      const docs = documentIds.map((id) => s.documents.byId[id]).filter(Boolean);
      return docs;
    },
    [documentIds]
  );

  // Use shallowEqual to prevent rerenders when the array contents haven't changed
  const relevantDocs = useAppSelector(relevantDocsSelector, (a, b) => {
    if (a.length !== b.length) return false;
    return a.every((doc, i) => doc === b[i]);
  });

  // Fetch and cache document metadata in Redux when missing using RTK Query
  const [fetchBatchDocuments, { data: batchDocumentsData }] = useLazyBatchDocumentsQuery();
  React.useEffect(() => {
    if (!personId || documentIds.length === 0) return;
    // Check against relevantDocs instead of entire documentsById to avoid unnecessary re-runs
    const loadedIds = new Set(relevantDocs.map((d) => d.documentId));
    const missing = documentIds.filter((id) => !loadedIds.has(id));
    if (missing.length === 0) return;

    // Use RTK Query lazy query to fetch missing documents
    fetchBatchDocuments({ userId: personId, ids: missing });
  }, [personId, documentIds, relevantDocs, fetchBatchDocuments]);

  // Process fetched documents and update Redux store
  React.useEffect(() => {
    if (!batchDocumentsData?.documents) return;
    const docs = batchDocumentsData.documents
      .map((d) => ({
        documentId: String(d.documentId || ''),
        name: String(d.name || ''),
        mimeType: d.mimeType,
        webViewUrl: d.webViewUrl,
        modifiedTime: d.modifiedTime,
      }))
      .filter((d) => d.documentId && d.name);
    if (docs.length > 0) {
      dispatch(upsertDocuments(docs));
    }
  }, [batchDocumentsData, dispatch]);

  // Create stable context object (only changes when documentIds or metadata change)
  // This prevents unnecessary WebSocket reconnections
  const chatContext = React.useMemo(() => {
    const baseContext: SummaryPageContext = {
      pageType: 'summary' as const,
      documentIds: documentIds,
      timestamp: Date.now(),
    };

    if (transcriptMetadata === null && documentIds.length > 0) {
      baseContext.artifactOrigin = ARTIFACT_ORIGIN_MANUAL_DRIVE;
    }

    // Include transcript metadata if available (for artifact title generation)
    if (transcriptMetadata) {
      baseContext.transcriptMetadata = transcriptMetadata;
    }

    return baseContext;
  }, [documentIds, transcriptMetadata]);

  // Select profile based on explicit query param or document count
  const profile = React.useMemo(() => {
    if (profileParam && profileParam.trim() !== '') {
      return profileParam;
    }
    const count = transcriptMetadata?.documentCount ?? documentIds.length;
    return getProfileForSummaryDocumentCount(count);
  }, [documentIds.length, profileParam, transcriptMetadata?.documentCount]);

  // Initialize chat with summary profile (properly typed)
  const chat: UseSidebarChatReturn = useSidebarChat({
    convId: resumableConversationID ?? undefined,
    profile: profile,
    autoConnect: true,
    displayMode: 'ui',
    context: chatContext,
  });

  const [draftSummary, setDraftSummary] = React.useState<string>('');
  // Use ref instead of state to prevent race conditions - ref updates are synchronous
  // so the guard is immediately effective even if callbacks change identity
  const hasAutoStartedRef = React.useRef<boolean>(false);
  // Store callback in ref to avoid dependency array issues with unstable callback identity
  const startWithHiddenMessageRef = React.useRef(chat.startWithHiddenMessage);
  React.useEffect(() => {
    startWithHiddenMessageRef.current = chat.startWithHiddenMessage;
  }, [chat.startWithHiddenMessage]);

  const [newVersionToast, setNewVersionToast] = React.useState<{ revision: number } | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [editSessionId, setEditSessionId] = React.useState<string | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [copyState, setCopyState] = React.useState<'idle' | 'copied' | 'error'>('idle');
  const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);
  const [feedbackType, setFeedbackType] = React.useState<FeedbackType | null>(null);
  const [feedbackError, setFeedbackError] = React.useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false);
  const [shareError, setShareError] = React.useState<string | null>(null);
  const [runFeedbackTypeByRunID, setRunFeedbackTypeByRunID] = React.useState<
    Record<string, FeedbackType>
  >({});
  const trackedViewRef = React.useRef(false);
  const lastPersistedToastRevisionRef = React.useRef<number | null>(null);
  const lastSyncedSelectionRef = React.useRef<string | null>(null);
  const hasInitializedSelectionFromUrlRef = React.useRef(false);

  const selection = useAppSelector(selectSummarySelection(chat.convId));
  const activeRun = useAppSelector(selectSummaryActiveRun(chat.convId));
  const isSummaryStreaming = useAppSelector(selectIsSummaryStreaming(chat.convId));
  const isHydratingSummaryReplay = useAppSelector(selectIsHydratingSummaryReplay(chat.convId));
  const isHydratingReplay = chat.isHydratingReplay || isHydratingSummaryReplay;

  const selectedArtifactId = selection?.artifactId ?? null;
  const selectedRevision = selection?.revision ?? null;
  const feedbackTarget = React.useMemo(
    () =>
      resolveFeedbackTarget({
        selectedArtifactId,
        activeRun,
      }),
    [activeRun, selectedArtifactId]
  );

  const {
    data: artifactData,
    isFetching: isArtifactFetching,
    error: artifactError,
  } = useGetArtifactQuery(
    selectedArtifactId && selectedRevision
      ? { id: selectedArtifactId, revision: selectedRevision }
      : skipToken,
    { refetchOnMountOrArgChange: true, refetchOnReconnect: true }
  );

  const [fetchArtifact] = useLazyGetArtifactQuery();
  const {
    data: versionsData,
    error: versionsError,
    isLoading: isVersionsLoading,
    refetch: refetchVersions,
  } = useListArtifactVersionsQuery(
    selectedArtifactId ? { id: selectedArtifactId, limit: 15 } : skipToken,
    {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );
  const [updateArtifact, { isLoading: isSaving }] = useUpdateArtifactMutation();
  const [shareArtifact, { isLoading: isSharing }] = useShareArtifactMutation();

  const latestRevision = versionsData?.versions?.[0]?.revision ?? null;
  const currentRevision = selectedRevision ?? latestRevision ?? 1;
  const typeLabel = getArtifactTypeLabel(profile);
  const summaryBreadcrumbLabel = React.useMemo(() => {
    const persistedTitle = artifactData?.title?.trim();
    if (persistedTitle) {
      return persistedTitle;
    }

    const fallbackTypeLabel = typeLabel.trim();
    if (fallbackTypeLabel.length === 0 || fallbackTypeLabel === 'Unknown artifact') {
      return null;
    }

    return fallbackTypeLabel;
  }, [artifactData?.title, typeLabel]);
  const versions = versionsData?.versions ?? [];
  const versionsErrorMessage = versionsError ? 'Failed to load versions.' : null;
  const disableDropdown =
    isEditing ||
    isSummaryStreaming ||
    chat.isStreaming ||
    isVersionsLoading ||
    !!versionsErrorMessage ||
    isArtifactFetching;

  const currentArtifactForContext = React.useMemo(() => {
    const editableEntities = chat.entities.filter(
      (e): e is EditableSummaryEntity => e.kind === 'editable_summary'
    );
    return toCurrentArtifactContext({
      artifactId: selectedArtifactId,
      profileSlug: profile,
      selectedRevision,
      latestRevision,
      editableEntities,
    });
  }, [chat.entities, latestRevision, profile, selectedArtifactId, selectedRevision]);

  const shareMemberId = transcriptMetadata?.subjectId ?? artifactData?.subject_id ?? null;
  const shareMember = teamData?.members?.find((member) => member.person.id === shareMemberId);
  const shareMemberEmail = shareMember?.person?.primary_email ?? '';
  const shareMemberName =
    transcriptMetadata?.subjectName?.trim() ||
    (shareMember ? getMemberDisplayName(shareMember) : 'Member');
  const shareSessionDate = React.useMemo(
    () => formatShareSessionDateFromTranscriptSessions(artifactData?.transcript_sessions),
    [artifactData?.transcript_sessions]
  );
  const shareProfileSlug = artifactData?.profile_slug ?? profile;
  const isCurrentProfileShareable = isProfileShareable(shareProfileSlug);
  const isCurrentVersionShared =
    artifactData?.shared_revision != null && artifactData.shared_revision === artifactData.revision;

  React.useEffect(() => {
    chat.updateContext({ currentArtifact: currentArtifactForContext ?? undefined });
  }, [chat.updateContext, currentArtifactForContext]);

  React.useEffect(() => {
    if (isEditing) return;
    setDraftSummary(artifactData?.markdown ?? '');
  }, [artifactData?.markdown, isEditing]);

  React.useEffect(() => {
    if (hasInitializedSelectionFromUrlRef.current) {
      return;
    }
    if (!hasCompleteArtifactRestoreState) {
      hasInitializedSelectionFromUrlRef.current = true;
      return;
    }
    if (!chat.convId) {
      return;
    }

    dispatch(
      setSelected({
        convId: chat.convId,
        selection: {
          artifactId: restoredArtifactID!,
          revision: restoredRevision!,
          source: 'unknown',
        },
      })
    );
    hasInitializedSelectionFromUrlRef.current = true;
  }, [
    chat.convId,
    dispatch,
    hasCompleteArtifactRestoreState,
    restoredArtifactID,
    restoredRevision,
  ]);

  React.useEffect(() => {
    if (!selection || !selectedArtifactId || selection.artifactId !== selectedArtifactId) {
      return;
    }

    const selectionKey = `${selection.artifactId}:${selection.revision}:${selection.requestedAt}`;
    if (lastSyncedSelectionRef.current === selectionKey) {
      return;
    }
    lastSyncedSelectionRef.current = selectionKey;
    void refetchVersions();
  }, [refetchVersions, selectedArtifactId, selection]);

  React.useEffect(() => {
    if (!chat.convId) return;
    if (!activeRun) return;
    if (!selection) return;
    if (activeRun.status === 'started' || activeRun.status === 'streaming') return;
    if (!activeRun.artifactId || !activeRun.revision) return;
    if (activeRun.artifactId !== selection.artifactId) return;
    if (activeRun.revision !== selection.revision) return;
    if (!artifactData) return;
    if (activeRun.streamingMarkdown.trim() === '') return;

    dispatch(clearActiveRun({ convId: chat.convId, stableId: activeRun.stableId }));
  }, [activeRun, artifactData, chat.convId, dispatch, selection]);

  // Map profile to artifact_type for analytics
  const artifactType = React.useMemo(() => profileSlugToArtifactType(profile), [profile]);

  // Fetch latest feedback only when artifact-target feedback is active.
  const { data: latestFeedback } = useGetLatestArtifactFeedbackQuery(
    { artifact_id: feedbackTarget?.kind === 'artifact' ? feedbackTarget.artifactId : '' },
    {
      skip: feedbackTarget?.kind !== 'artifact',
      refetchOnMountOrArgChange: true,
    }
  );
  const latestFeedbackType =
    feedbackTarget?.kind === 'artifact'
      ? latestFeedback?.feedback?.feedback_type || null
      : feedbackTarget?.kind === 'run'
        ? runFeedbackTypeByRunID[feedbackTarget.runId] || null
        : null;

  const [createFeedback, { isLoading: isSubmittingFeedback }] = useCreateArtifactFeedbackMutation();

  /**
   * Generates the initial prompt text for auto-starting artifact generation conversations.
   *
   * IMPORTANT: When adding a new artifact type, you MUST update this function to handle it.
   * This ensures the user-facing prompt text matches the artifact type's purpose.
   *
   * @param profile - The artifact profile slug (e.g., 'coaching-feedback', 'coaching-conversation-summary')
   * @param documentIds - Array of document IDs to include in the prompt
   * @returns The initial prompt text to send to start the conversation
   */
  const getInitialPromptForProfile = React.useCallback(
    (profile: string, documentIds: string[]): string => {
      const docList = documentIds.join(', ');

      // Handle each artifact type explicitly to ensure all are covered
      switch (profile) {
        case PROFILE_SLUGS.COACHING_FEEDBACK:
        case PROFILE_SLUGS.COACHING_FEEDBACK_ARC:
          return `Please provide coaching reflection for the following documents: ${docList}`;
        case PROFILE_SLUGS.CHEM_CALL_REFLECTION:
          return `Please provide chem call reflection for the following documents: ${docList}`;
        case PROFILE_SLUGS.CHEM_CALL_REFLECTION_MEMBER:
          return `Please provide chemistry call reflection for the following documents: ${docList}`;
        case PROFILE_SLUGS.MEMBER_REFLECTION:
        case PROFILE_SLUGS.MEMBER_REFLECTION_ARC:
          return `Please provide member reflection for the following documents: ${docList}`;
        case PROFILE_SLUGS.SESSION_PREP_COACH:
          return `Please create session prep for the following documents: ${docList}`;
        case PROFILE_SLUGS.SESSION_PREP_MEMBER:
          return `Please create member session prep for the following documents: ${docList}`;
        case PROFILE_SLUGS.MILESTONES_AND_WINS:
          return `Please generate milestones and wins for the following documents: ${docList}`;
        case PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY:
        case PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY:
        case PROFILE_SLUGS.DRIVE1ON1_SUMMARY:
        default:
          // Default fallback for summary-type artifacts
          return `Please summarize the following documents: ${docList}`;
      }
    },
    []
  );

  // Auto-start conversation when connected and documents are present
  // Uses refs for guard and callback to prevent race conditions from state/callback identity changes
  React.useEffect(() => {
    if (
      hasAutoStartedRef.current ||
      hasResumableConversation ||
      !chat.isConnected ||
      documentIds.length === 0 ||
      chat.entities.length > 0
    ) {
      return;
    }
    // Set guard BEFORE async call to prevent duplicate runs
    hasAutoStartedRef.current = true;
    const initialPrompt = getInitialPromptForProfile(profile, documentIds);
    startWithHiddenMessageRef.current(initialPrompt).catch((e: Error) => {
      console.error('Failed to start summary conversation:', e);
      // Reset guard on failure so user can retry
      hasAutoStartedRef.current = false;
    });
  }, [
    chat.isConnected,
    chat.entities.length,
    documentIds,
    hasResumableConversation,
    profile,
    getInitialPromptForProfile,
  ]);

  React.useEffect(() => {
    if (isHydratingReplay) {
      return;
    }
    const canonicalConversationID = parseCanonicalConversationID(chat.convId);
    if (!canonicalConversationID) {
      return;
    }
    if (searchParams.get('conversation_id') === canonicalConversationID) {
      return;
    }

    setSearchParams(
      (prevSearchParams) => {
        if (prevSearchParams.get('conversation_id') === canonicalConversationID) {
          return prevSearchParams;
        }
        const nextSearchParams = new URLSearchParams(prevSearchParams);
        nextSearchParams.set('conversation_id', canonicalConversationID);
        return nextSearchParams;
      },
      { replace: true }
    );
  }, [chat.convId, isHydratingReplay, searchParams, setSearchParams]);

  React.useEffect(() => {
    if (!hasInitializedSelectionFromUrlRef.current) {
      return;
    }
    if (isHydratingReplay) {
      return;
    }

    const currentArtifactID = searchParams.get('artifact_id');
    const currentRevision = searchParams.get('revision');

    if (selectedArtifactId && selectedRevision) {
      const nextRevision = String(selectedRevision);
      if (currentArtifactID === selectedArtifactId && currentRevision === nextRevision) {
        return;
      }
      setSearchParams(
        (prevSearchParams) => {
          const prevArtifactID = prevSearchParams.get('artifact_id');
          const prevRevision = prevSearchParams.get('revision');
          if (prevArtifactID === selectedArtifactId && prevRevision === nextRevision) {
            return prevSearchParams;
          }
          const nextSearchParams = new URLSearchParams(prevSearchParams);
          nextSearchParams.set('artifact_id', selectedArtifactId);
          nextSearchParams.set('revision', nextRevision);
          return nextSearchParams;
        },
        { replace: true }
      );
      return;
    }

    if (currentArtifactID === null && currentRevision === null) {
      return;
    }

    setSearchParams(
      (prevSearchParams) => {
        const prevArtifactID = prevSearchParams.get('artifact_id');
        const prevRevision = prevSearchParams.get('revision');
        if (prevArtifactID === null && prevRevision === null) {
          return prevSearchParams;
        }
        const nextSearchParams = new URLSearchParams(prevSearchParams);
        nextSearchParams.delete('artifact_id');
        nextSearchParams.delete('revision');
        return nextSearchParams;
      },
      { replace: true }
    );
  }, [isHydratingReplay, searchParams, selectedArtifactId, selectedRevision, setSearchParams]);

  // Redirect if no documents selected
  React.useEffect(() => {
    if (documentIds.length === 0) {
      navigate('/members');
    }
  }, [documentIds, navigate]);

  React.useEffect(() => {
    if (trackedViewRef.current) return;
    const props: Record<string, unknown> = {
      profile_slug: profile,
      artifact_type_label: typeLabel,
    };
    if (selectedArtifactId) {
      props.artifact_id = selectedArtifactId;
    }
    analytics.viewed('Viewed artifact', 'summary', location.pathname, props);
    trackedViewRef.current = true;
  }, [location.pathname, profile, selectedArtifactId, typeLabel]);

  const handleSendMessage = React.useCallback((text: string) => chat.sendMessage(text), [chat]);

  const handleEditingChange = React.useCallback(
    (nextIsEditing: boolean) => {
      setIsEditing(nextIsEditing);
      if (!chat.convId) return;
      dispatch(setEditing({ convId: chat.convId, isEditing: nextIsEditing }));
    },
    [chat.convId, dispatch]
  );

  const handleEditStart = React.useCallback(() => {
    if (!selectedArtifactId) return;
    if (isSummaryStreaming) return;
    const newEditSessionId = crypto.randomUUID();
    setEditSessionId(newEditSessionId);
    const props: Record<string, unknown> = {
      profile_slug: profile,
      artifact_type_label: typeLabel,
      edit_session_id: newEditSessionId,
      from_screen: 'summary',
    };
    props.artifact_id = selectedArtifactId;
    analytics.track('Edited artifact', props);
  }, [isSummaryStreaming, profile, selectedArtifactId, typeLabel]);

  const handleCancelEdit = React.useCallback(() => {
    const props: Record<string, unknown> = {
      profile_slug: profile,
      artifact_type_label: typeLabel,
    };
    if (selectedArtifactId) {
      props.artifact_id = selectedArtifactId;
    }
    if (editSessionId) {
      props.edit_session_id = editSessionId;
    }
    analytics.track('Canceled edit artifact', props);
  }, [editSessionId, profile, selectedArtifactId, typeLabel]);

  const handleCancelEditWrapper = React.useCallback(() => {
    setDraftSummary(artifactData?.markdown ?? '');
    setEditSessionId(null);
    handleEditingChange(false);
    handleCancelEdit();
  }, [artifactData?.markdown, handleCancelEdit, handleEditingChange]);

  React.useEffect(() => {
    if (copyState === 'idle') return;
    const timeout = window.setTimeout(() => setCopyState('idle'), 2000);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  // Toast on newly persisted versions (when we auto-selected due to artifact.persisted)
  React.useEffect(() => {
    if (!selection) return;
    if (selection.source !== 'persisted') return;
    if (lastPersistedToastRevisionRef.current === null) {
      lastPersistedToastRevisionRef.current = selection.revision;
      return;
    }
    if (selection.revision > lastPersistedToastRevisionRef.current) {
      setNewVersionToast({ revision: selection.revision });
    }
    lastPersistedToastRevisionRef.current = selection.revision;
  }, [selection]);

  // Handle 409 revision conflict by fetching latest and retrying once
  const handleRevisionConflict = React.useCallback(
    async (editedMarkdown: string): Promise<string | null> => {
      if (!selectedArtifactId || !personId) {
        return 'Missing artifact ID or person ID';
      }

      try {
        const latest = await fetchArtifact({ id: selectedArtifactId }).unwrap();

        const retryData = await updateArtifact({
          id: selectedArtifactId,
          markdown: editedMarkdown,
          revision: latest.revision,
          actor_id: personId,
          ...(editSessionId ? { edit_session_id: editSessionId } : {}),
        }).unwrap();

        dispatch(
          setSelected({
            convId: chat.convId!,
            selection: {
              artifactId: selectedArtifactId,
              revision: retryData.revision,
              source: 'dropdown',
              requestedAt: Date.now(),
            },
          })
        );
        handleEditingChange(false);

        return null;
      } catch (error) {
        const status = (error as FetchBaseQueryError)?.status;
        if (status && typeof status === 'number') {
          return `Retry failed: ${status}. Please reload and try again.`;
        }
        return 'Conflict resolution failed. Please reload the page and try again.';
      }
    },
    [
      chat.convId,
      dispatch,
      editSessionId,
      fetchArtifact,
      handleEditingChange,
      personId,
      selectedArtifactId,
      updateArtifact,
    ]
  );

  const handleSummaryChange = React.useCallback((value: string) => {
    setDraftSummary(value);
    setSaveError(null); // Clear errors when user edits
  }, []);

  const handleVersionSelect = React.useCallback(
    (revision: number) => {
      if (!chat.convId) return;
      if (!selectedArtifactId) return;
      const props: Record<string, unknown> = {
        profile_slug: profile,
        artifact_type_label: typeLabel,
        revision,
      };
      props.artifact_id = selectedArtifactId;
      analytics.track('Selected artifact version', props);
      setSaveError(null);

      dispatch(
        setSelected({
          convId: chat.convId,
          selection: { artifactId: selectedArtifactId, revision, source: 'dropdown' },
        })
      );
    },
    [chat.convId, dispatch, profile, selectedArtifactId, typeLabel]
  );

  const handleSave = React.useCallback(
    async (editedMarkdown: string) => {
      if (!chat.convId || !selectedArtifactId || !selectedRevision || !personId) {
        const error = 'Cannot save: missing conversation, artifact selection, or person ID';
        console.error(error);
        setSaveError(error);
        throw new Error(error);
      }

      const props: Record<string, unknown> = {
        profile_slug: profile,
        artifact_type_label: typeLabel,
      };
      props.artifact_id = selectedArtifactId;
      if (editSessionId) {
        props.edit_session_id = editSessionId;
      }
      analytics.track('Saved artifact', props);

      setSaveError(null);

      try {
        const data = await updateArtifact({
          id: selectedArtifactId,
          markdown: editedMarkdown,
          revision: selectedRevision,
          actor_id: personId,
          ...(editSessionId ? { edit_session_id: editSessionId } : {}),
        }).unwrap();

        dispatch(
          setSelected({
            convId: chat.convId,
            selection: {
              artifactId: selectedArtifactId,
              revision: data.revision,
              source: 'dropdown',
            },
          })
        );
        handleEditingChange(false);

        // Clear edit session ID after successful save
        setEditSessionId(null);

        // Optional: Show success toast
        console.log('Summary saved successfully');
      } catch (error) {
        const status = (error as FetchBaseQueryError)?.status;
        if (status === 404) {
          const message = 'Artifact not found. It may not have been created yet.';
          setSaveError(message);
          throw new Error(message);
        }
        if (status === 409) {
          const retryError = await handleRevisionConflict(editedMarkdown);
          if (retryError) {
            setSaveError(retryError);
            throw new Error(retryError);
          }
          return;
        }
        if (status && typeof status === 'number') {
          const message = `Save failed: ${status}`;
          setSaveError(message);
          throw new Error(message);
        }
        console.error('Failed to save artifact:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Save failed. Please try again.';
        setSaveError(errorMessage);
        // Re-throw to ensure EditableSummary keeps edit mode open
        throw error;
      }
    },
    [
      chat.convId,
      dispatch,
      editSessionId,
      handleRevisionConflict,
      handleEditingChange,
      personId,
      profile,
      selectedArtifactId,
      selectedRevision,
      typeLabel,
      updateArtifact,
    ]
  );

  const handleOpenFeedback = React.useCallback(
    (type: FeedbackType) => {
      if (!feedbackTarget) return;
      setFeedbackType(type);
      setFeedbackError(null);
      setFeedbackModalOpen(true);
    },
    [feedbackTarget]
  );

  const handleCloseFeedback = React.useCallback(() => {
    setFeedbackModalOpen(false);
    setFeedbackError(null);
  }, []);

  const handleOpenShareDialog = React.useCallback(() => {
    setShareError(null);
    setShareDialogOpen(true);
  }, []);

  const handleCloseShareDialog = React.useCallback(() => {
    setShareDialogOpen(false);
    setShareError(null);
  }, []);

  const handleConfirmShare = React.useCallback(async () => {
    if (!isCurrentProfileShareable) {
      setShareError('This artifact type cannot be shared');
      return;
    }
    if (!selectedArtifactId || !shareMemberEmail) {
      setShareError('Unable to share: missing artifact or member information');
      return;
    }

    try {
      await shareArtifact({
        id: selectedArtifactId,
        recipient_email: shareMemberEmail,
      }).unwrap();

      setShareDialogOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to share artifact. Please try again.';
      setShareError(message);
    }
  }, [isCurrentProfileShareable, selectedArtifactId, shareArtifact, shareMemberEmail]);

  const handleSubmitFeedback = React.useCallback(
    async (feedbackText: string) => {
      if (!feedbackTarget || !feedbackType) return;

      setFeedbackError(null);
      try {
        const feedbackContext: Record<string, unknown> = {
          version: 1,
        };
        if (chat.convId) {
          feedbackContext.conversation_id = chat.convId;
        }
        if (feedbackTarget.kind === 'artifact') {
          feedbackContext.artifact_id = feedbackTarget.artifactId;
        } else {
          feedbackContext.run_id = feedbackTarget.runId;
        }
        await createFeedback({
          feedback_type: feedbackType,
          feedback_text: feedbackText || undefined,
          context: feedbackContext,
        }).unwrap();
        if (feedbackTarget.kind === 'run') {
          setRunFeedbackTypeByRunID((prev) => ({
            ...prev,
            [feedbackTarget.runId]: feedbackType,
          }));
        }

        // Track analytics event
        analytics.track('User Submitted Feedback', {
          profile_slug: profile,
          artifact_type_label: typeLabel,
          feedback_type: feedbackType,
          feedback_text: feedbackText || '',
          feedback_target_type: feedbackTarget.kind,
          artifact_id: feedbackTarget.kind === 'artifact' ? feedbackTarget.artifactId : undefined,
          run_id: feedbackTarget.kind === 'run' ? feedbackTarget.runId : undefined,
          artifact_type: artifactType || profile || 'unknown',
          has_text: feedbackText.trim().length > 0,
          text_length: feedbackText.length,
        });

        // Close modal on success
        setFeedbackModalOpen(false);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to submit feedback. Please try again.';
        setFeedbackError(message);
        // Keep modal open on error
      }
    },
    [feedbackTarget, artifactType, profile, feedbackType, createFeedback, typeLabel, chat.convId]
  );

  const persistedMarkdown = artifactData?.markdown ?? '';
  const streamingMarkdown = activeRun?.streamingMarkdown ?? '';
  const shouldShowStreaming = shouldShowStreamingSummary({
    streamingMarkdown,
    isSummaryStreaming,
    activeRun,
    selection,
    artifactDataRevision: artifactData?.revision ?? null,
  });
  const pendingVersionGeneration = isAwaitingPersistedVersion(activeRun);
  const displayValue = shouldShowStreaming ? streamingMarkdown : persistedMarkdown;
  const hasContent =
    displayValue.trim() !== '' ||
    (!!selectedArtifactId && !!selectedRevision && isArtifactFetching);
  const chatErrorMessage =
    typeof chat.error === 'string' && chat.error.trim() !== ''
      ? chat.error
      : chat.error
        ? 'Failed to start summary.'
        : null;

  const handleCopySummary = React.useCallback(async () => {
    if (!displayValue) return;

    try {
      await copyMarkdownToClipboard(displayValue);
      setCopyState('copied');
      const props: Record<string, unknown> = {
        profile_slug: profile,
        artifact_type_label: typeLabel,
      };
      if (selectedArtifactId) {
        props.artifact_id = selectedArtifactId;
      }
      analytics.track('Copied artifact', props);
    } catch {
      setCopyState('error');
    }
  }, [displayValue, profile, selectedArtifactId, typeLabel]);

  if (documentIds.length === 0) {
    return <div>Redirecting...</div>;
  }

  const rightSidebar = (
    <SidebarChat
      storageId="summary-assistant"
      entities={chat.entities}
      convId={chat.convId}
      onSendMessage={handleSendMessage}
      isLoading={chat.isStreaming}
      placeholder="Ask questions or request changes..."
      showHeader={false}
    />
  );

  return (
    <>
      <ArtifactPageWithChatLayout rightSidebar={rightSidebar}>
        <SummaryBreadcrumbOverrides
          memberLabel={summaryMemberLabel}
          memberId={transcriptMetadata?.subjectId ?? null}
          summaryLabel={summaryBreadcrumbLabel}
        />
        <div className="flex flex-col gap-4 h-full">
          {/* Main content area */}
          <div className="flex-1 flex flex-col gap-4 min-h-0">
            {/* Summary Display */}
            <Paper className="p-4 flex-1 min-h-0 overflow-auto flex flex-col">
              <div className="flex flex-col gap-3 h-full min-h-0">
                {/* Header line: version dropdown (left) + copy/edit icons (right) */}
                <div className="flex items-center justify-between gap-3 mb-4">
                  {selectedArtifactId ? (
                    <ArtifactVersionDropdown
                      typeLabel={typeLabel}
                      currentRevision={currentRevision}
                      versions={versions}
                      onVersionSelect={handleVersionSelect}
                      disabled={disableDropdown}
                      pendingGeneration={pendingVersionGeneration}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontSize: '1.125rem', fontWeight: 500 }}>
                      {typeLabel}
                    </Typography>
                  )}
                  <div className="flex items-center gap-1">
                    {isEditing ? (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={async () => {
                            try {
                              await handleSave(draftSummary);
                            } catch {
                              // Error handled by handleSave
                            }
                          }}
                          disabled={
                            !selectedArtifactId ||
                            isSaving ||
                            draftSummary === (artifactData?.markdown ?? '')
                          }
                          size="small"
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <IconButton
                          aria-label="Cancel edit"
                          size="small"
                          onClick={handleCancelEditWrapper}
                          disabled={!selectedArtifactId || isSaving}
                        >
                          <Icon fontSize="small">close</Icon>
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <Tooltip
                          title={
                            !displayValue
                              ? 'Nothing to copy'
                              : copyState === 'copied'
                                ? 'Copied!'
                                : copyState === 'error'
                                  ? 'Copy failed'
                                  : 'Copy summary'
                          }
                        >
                          <span>
                            <IconButton
                              aria-label="Copy summary to clipboard"
                              size="small"
                              onClick={handleCopySummary}
                              disabled={!displayValue}
                            >
                              <Icon fontSize="small">content_copy</Icon>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={feedbackTarget ? 'What works well' : 'Generating summary...'}
                        >
                          <span>
                            <IconButton
                              aria-label="Thumbs up"
                              size="small"
                              onClick={() => handleOpenFeedback('positive')}
                              disabled={!feedbackTarget}
                              sx={{
                                color:
                                  latestFeedbackType === 'positive' ? 'primary.main' : 'inherit',
                              }}
                            >
                              <Icon fontSize="small">
                                {latestFeedbackType === 'positive'
                                  ? 'thumb_up'
                                  : 'thumb_up_off_alt'}
                              </Icon>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            feedbackTarget ? 'What could be improved' : 'Generating summary...'
                          }
                        >
                          <span>
                            <IconButton
                              aria-label="Thumbs down"
                              size="small"
                              onClick={() => handleOpenFeedback('negative')}
                              disabled={!feedbackTarget}
                              sx={{
                                color:
                                  latestFeedbackType === 'negative' ? 'primary.main' : 'inherit',
                              }}
                            >
                              <Icon fontSize="small">
                                {latestFeedbackType === 'negative'
                                  ? 'thumb_down'
                                  : 'thumb_down_off_alt'}
                              </Icon>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            !selectedArtifactId
                              ? 'Generating summary...'
                              : isSummaryStreaming || chat.isStreaming
                                ? 'Summary is still being generated...'
                                : 'Edit summary'
                          }
                        >
                          <span>
                            <IconButton
                              aria-label="Edit summary"
                              size="small"
                              onClick={() => {
                                setDraftSummary(persistedMarkdown);
                                handleEditingChange(true);
                                handleEditStart();
                              }}
                              disabled={
                                !selectedArtifactId || isSummaryStreaming || chat.isStreaming
                              }
                            >
                              <Icon fontSize="small">edit</Icon>
                            </IconButton>
                          </span>
                        </Tooltip>
                        {isCurrentProfileShareable && (
                          <ShareButton
                            isShared={!!isCurrentVersionShared}
                            onClick={handleOpenShareDialog}
                            disabled={!selectedArtifactId || !artifactData || !shareMemberEmail}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>

                {!chat.isConnected && (
                  <div className="flex items-center gap-2 p-3 text-sm text-amber-600 bg-amber-50 rounded-lg">
                    <Icon>hourglass_empty</Icon>
                    <span>Connecting to assistant...</span>
                  </div>
                )}

                {versionsErrorMessage ? (
                  <Alert severity="error">{versionsErrorMessage}</Alert>
                ) : null}
                {artifactError ? <Alert severity="error">Failed to load summary.</Alert> : null}
                {chatErrorMessage ? <Alert severity="error">{chatErrorMessage}</Alert> : null}

                {/* Only show empty states when we truly have no content */}
                {!hasContent && (
                  <>
                    {chat.isConnected && !chat.isStreaming && !chatErrorMessage && (
                      <div className="flex flex-col items-center justify-center flex-1 text-neutral-500">
                        <Icon className="text-5xl mb-2">article</Icon>
                        <Typography variant="body2">No summary yet</Typography>
                        <Typography variant="caption">
                          The assistant is analyzing your documents...
                        </Typography>
                      </div>
                    )}

                    {chat.isStreaming && !chatErrorMessage && (
                      <div className="flex flex-col items-center justify-center flex-1 text-neutral-500">
                        <Icon className="text-5xl mb-2 animate-spin">refresh</Icon>
                        <Typography variant="body2">Analyzing documents...</Typography>
                        <Typography variant="caption">This may take a moment</Typography>
                      </div>
                    )}
                  </>
                )}

                {saveError && (
                  <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                    {saveError}
                  </Typography>
                )}

                {/* Once we have content, always show it (even while streaming more) */}
                {hasContent && (
                  <div className="flex-1 min-h-0 overflow-auto mp-block artifact-content">
                    <EditableSummary
                      value={isEditing ? draftSummary : displayValue}
                      chunks={[]}
                      onChange={handleSummaryChange}
                      onSave={handleSave}
                      onEditStart={handleEditStart}
                      onCancelEdit={handleCancelEditWrapper}
                      onCopy={handleCopySummary}
                      readOnly={!selectedArtifactId || isSummaryStreaming || chat.isStreaming}
                      isSaving={isSaving}
                      isLoadingRevision={isArtifactFetching}
                      saveError={saveError}
                      showEditButton={false}
                      isEditing={isEditing}
                      onEditingChange={handleEditingChange}
                      feedback={
                        selectedArtifactId
                          ? {
                              artifactId: selectedArtifactId,
                              artifactType: artifactType || profile || 'unknown',
                              latestFeedbackType,
                              onOpen: handleOpenFeedback,
                            }
                          : undefined
                      }
                    />
                  </div>
                )}
              </div>
            </Paper>
          </div>
        </div>
        <FeedbackModal
          open={feedbackModalOpen}
          feedbackType={feedbackType}
          onClose={handleCloseFeedback}
          onSubmit={handleSubmitFeedback}
          isSubmitting={isSubmittingFeedback}
          error={feedbackError}
        />
        <ShareConfirmationDialog
          open={shareDialogOpen}
          memberName={shareMemberName}
          sessionDate={shareSessionDate}
          version={artifactData?.revision ?? currentRevision}
          onClose={handleCloseShareDialog}
          onConfirm={handleConfirmShare}
          isSharing={isSharing}
          error={shareError}
        />
      </ArtifactPageWithChatLayout>
      <Snackbar
        open={!!newVersionToast}
        autoHideDuration={3000}
        onClose={() => setNewVersionToast(null)}
        message={newVersionToast ? `New version generated: v${newVersionToast.revision}` : ''}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </>
  );
}
